import React, { useEffect, useMemo } from "react";
import { StyleSheet } from "react-native";
import { View, Wrapper } from "@components/atoms/Themed";
import NavigationBar from "@components/molecules/NavigationBar";
import SafeAreaView from "@components/molecules/SafeAreaView";
import useExecptionalError from "@hooks/useExecptionalError";
import Upgrade from "@components/organisms/Fallback/FallbackUpdate";
import Maintenance from "@components/organisms/Fallback/FallbackMaintenance";
import Expired from "@components/organisms/Fallback/FallbackExpired";
import Network from "@components/organisms/Fallback/FallbackNetwork";
import ServiceDown from "@components/organisms/Fallback/FallbackServiceDown";
import Unknown from "@components/organisms/Fallback/FallbackUnknown";
import { notify, sendError } from "@lib/util/logger";
import { slackWebhookUrl } from "@constants/Env";
import useAccount from "@hooks/useAccount";
import useUserDatabaseId from "@hooks/useUserDatabaseId";
import ExceptionError from "@lib/errors/exceptionError";

export default function Fallback() {
  const { state: error } = useExecptionalError();
  const { state: influencerId } = useAccount();
  const { state: userDatabaseId } = useUserDatabaseId();

  const title = useMemo<string>(() => {
    if (error === null) {
      return "エラーが発生しています";
    }
    switch (error.code) {
      case "Upgrade":
        return "アップデートのお知らせ";
      case "Maintenance":
        return "メンテナンスのお知らせ";
      case "Expired":
        return "ログインが切れました";
      case "NetworkError":
        return "オフラインになっています";
      case "ServiceDown":
        return "サービスがダウンしています";
      case "Unknown":
      default:
        return "エラーが発生しています";
    }
  }, [error]);

  useEffect(() => {
    if (error !== null) {
      if (
        error.code === undefined ||
        ["ServiceDown", "Unknown"].indexOf(error.code) !== -1
      ) {
        sendError(new ExceptionError("Fallback error", error));
      }
    }
  }, [error]);

  // slackに通知する
  useEffect(() => {
    if (error != null && slackWebhookUrl !== "") {
      notify(
        {
          ...error,
          influencerId,
          userDatabaseId,
        },
        { level: "error" }
      );
    }
  }, [error, influencerId, userDatabaseId]);

  if (error === null) {
    return null;
  }

  return (
    <SafeAreaView>
      <View style={styles.container}>
        <NavigationBar title={title} />
        <Wrapper>
          {error.code === "Upgrade" ? (
            <Upgrade />
          ) : error.code === "Maintenance" ? (
            <Maintenance />
          ) : error.code === "Expired" ? (
            <Expired />
          ) : error.code === "NetworkError" ? (
            <Network />
          ) : error.code === "ServiceDown" ? (
            <ServiceDown error={error} />
          ) : (
            <Unknown message={error.message} />
          )}
        </Wrapper>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
